import { FieldError, useFormContext, useWatch } from 'react-hook-form'
import { Skeleton, Text } from '@lounge-fe/ui-kit'
import SelectGroup from '../SelectGroup'
import classNames from 'classnames'
import { isOptionInStock } from '../utils'
import { VariantOptionsProps } from './types'
import { GiftCardDesignsCarousel } from '../GiftCardDesignsCarousel/GiftCardDesignsCarousel'
import { DESIGNS_NAME } from '../hooks/useGiftCard'
import { SizeGuideModal } from '../SizeGuideModal'
import { useDataTranslation } from '@/features/localisation/hooks/useDataTranslation/useDataTranslation'

export const VariantSelection = ({
  options,
  variants,
  getIsSelectOption,
  isDisabledSelectable,
  sizeGuides,
  sizeGuidePrimaryAction,
  sizeGuideSecondaryAction,
}: VariantOptionsProps) => {
  const {
    formState: { errors },
  } = useFormContext<{ options: FieldError[] }>()

  const selectedOptions = useWatch({ name: 'options' })
  const { isLoading: isDataTranslationLoading, t } = useDataTranslation()

  return (
    <div
      data-testid="variant-selection"
      className={classNames('flex flex-wrap gap-4 relative')}
    >
      {sizeGuides && (
        <SizeGuideModal
          buttonClassName="absolute top-0 right-0 leading-sm"
          sizeGuides={sizeGuides}
          primaryAction={sizeGuidePrimaryAction}
          secondaryAction={sizeGuideSecondaryAction}
        />
      )}
      {options.map(({ name, values }, idx) => {
        const isSelectOption = getIsSelectOption(name)
        const translatedTitle = !isDataTranslationLoading ? (
          t(name)
        ) : (
          <Skeleton>
            <Skeleton.Block className="w-full max-w-[150px] h-5" />
          </Skeleton>
        )

        if (name.toLowerCase() === DESIGNS_NAME) {
          return (
            <GiftCardDesignsCarousel
              key={name}
              name={`options.${idx}`}
              options={values}
              renderTitle={({ currentOption }) => (
                <Text variant="body-lg" className="mb-1">
                  <b className="font-regular">{translatedTitle}:</b>{' '}
                  {currentOption}
                </Text>
              )}
              variants={variants}
              index={idx}
              selectedId={selectedOptions[idx]}
            />
          )
        }

        const isEvenSelect = isSelectOption && idx % 2 === 0

        return (
          <SelectGroup
            key={name}
            className={
              isSelectOption ? 'flex-[1_0_calc(50%-16px)]' : 'flex-[1_0_100%]'
            }
            title={isSelectOption ? t('product.select_size') : translatedTitle}
            showTitle={isSelectOption ? isEvenSelect : true}
            fields={[
              {
                label: translatedTitle,
                name: `options.${idx}`,
                isDisabledSelectable,
                options: values.map((option) => ({
                  label: option,
                  value: option,
                  disabled: !isOptionInStock(
                    option,
                    idx,
                    selectedOptions,
                    variants,
                  ),
                })),
                errors,
                control: isSelectOption ? 'select' : 'choicechip',
              },
            ]}
          />
        )
      })}
    </div>
  )
}
